import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  bookManufacturingOverheadPeriodActual,
  finalizeManufacturingPeriodClose,
  getManufacturingMaterialsReconciliation,
  getManufacturingOverheadPeriodSummary,
  getManufacturingOverheadPeriodVariance,
  listManufacturingPlants,
  reopenManufacturingPeriodClose,
  setManufacturingOverheadPeriodAssumed,
  startManufacturingPeriodClose,
} from "@/lib/manufacturingClient";
import type { MfgMaterialsReconciliationReport } from "@/lib/manufacturing/materialIssuanceClient";
import type {
  MfgOverheadPeriodBucketRow,
  MfgOverheadPeriodSummary,
  MfgOverheadPeriodVarianceReport,
} from "@/lib/manufacturing/overheadTypes";
import { draftsForLoadedPeriod, type OverheadPeriodDraft } from "@/lib/manufacturing/overheadPeriodDrafts";
import type { MfgPlant } from "@/lib/manufacturing/types";

function defaultPeriodStartIso(now = new Date()): string {
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, "0")}-01`;
}

function periodStartFromMonthInput(value: string): string {
  if (!value) return defaultPeriodStartIso();
  return `${value}-01`;
}

function monthInputFromPeriodStart(periodStart: string): string {
  return periodStart.slice(0, 7);
}

export type { OverheadPeriodDraft } from "@/lib/manufacturing/overheadPeriodDrafts";

export function useManufacturingOverheadPeriodValues(enabled: boolean, navTick: number) {
  const [plants, setPlants] = useState<MfgPlant[]>([]);
  const [plantId, setPlantId] = useState("");
  const [periodMonth, setPeriodMonth] = useState(() => monthInputFromPeriodStart(defaultPeriodStartIso()));
  const [summary, setSummary] = useState<MfgOverheadPeriodSummary | null>(null);
  const [varianceReport, setVarianceReport] = useState<MfgOverheadPeriodVarianceReport | null>(null);
  const [materialsReconciliation, setMaterialsReconciliation] =
    useState<MfgMaterialsReconciliationReport | null>(null);
  const [draftState, setDraftState] = useState<{ period: string; drafts: Record<string, OverheadPeriodDraft> }>({
    period: "",
    drafts: {},
  });
  const [reopenReason, setReopenReason] = useState("");
  const [copyActualsOnFinalize, setCopyActualsOnFinalize] = useState(false);
  const [listBusy, setListBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const periodStart = useMemo(() => periodStartFromMonthInput(periodMonth), [periodMonth]);
  const drafts = draftState.period === periodStart ? draftState.drafts : {};
  const summaryRequestRef = useRef(0);

  const reloadPlants = useCallback(async () => {
    if (!enabled) return;
    const r = await listManufacturingPlants();
    if (!r.ok) return;
    setPlants(r.data.plants);
    setPlantId((prev) => {
      if (prev) return prev;
      const def = r.data.plants.find((p) => p.is_default) ?? r.data.plants[0];
      return def?.id ?? "";
    });
  }, [enabled]);

  const reloadSummary = useCallback(async () => {
    if (!enabled || !plantId) return;
    const requestedPeriod = periodStart;
    const requestId = ++summaryRequestRef.current;
    setListBusy(true);
    setLocalError(null);
    const [summaryRes, varianceRes, reconRes] = await Promise.all([
      getManufacturingOverheadPeriodSummary({ plant_id: plantId, period_start: requestedPeriod }),
      getManufacturingOverheadPeriodVariance({ plant_id: plantId, period_start: requestedPeriod }),
      getManufacturingMaterialsReconciliation({ plant_id: plantId, period_start: requestedPeriod }),
    ]);
    if (summaryRequestRef.current !== requestId) return;
    setListBusy(false);
    if (!summaryRes.ok) {
      setLocalError(summaryRes.error);
      return;
    }
    setSummary(summaryRes.data.summary);
    setVarianceReport(varianceRes.ok ? varianceRes.data.report : null);
    setMaterialsReconciliation(reconRes.ok ? reconRes.data.report : null);
    setDraftState((prev) => ({
      period: requestedPeriod,
      drafts: draftsForLoadedPeriod(
        prev.period === requestedPeriod ? prev.drafts : {},
        requestedPeriod,
        requestedPeriod,
        summaryRes.data.summary.rows,
      ),
    }));
  }, [enabled, periodStart, plantId]);

  useEffect(() => {
    void reloadPlants();
  }, [reloadPlants, navTick]);

  useEffect(() => {
    void reloadSummary();
  }, [reloadSummary, navTick]);

  function setDraft(bucketId: string, patch: Partial<OverheadPeriodDraft>) {
    setDraftState((prev) => {
      const current = prev.period === periodStart ? prev.drafts : {};
      return {
        period: periodStart,
        drafts: {
          ...current,
          [bucketId]: {
            assumed: current[bucketId]?.assumed ?? "",
            actual: current[bucketId]?.actual ?? "",
            reason: current[bucketId]?.reason ?? "",
            ...patch,
          },
        },
      };
    });
  }

  async function saveAssumed(row: MfgOverheadPeriodBucketRow) {
    if (!plantId) return;
    const draft = drafts[row.bucket_id];
    const reason = draft?.reason.trim() ?? "";
    if (!reason) {
      setLocalError("Assumed update reason is required for audit trail.");
      return;
    }
    const parsed = draft?.assumed.trim() === "" ? null : Number(draft?.assumed);
    if (parsed !== null && (!Number.isFinite(parsed) || parsed < 0)) {
      setLocalError("Assumed value must be a non-negative number or blank.");
      return;
    }

    setActionBusy(true);
    setLocalError(null);
    const r = await setManufacturingOverheadPeriodAssumed({
      plant_id: plantId,
      bucket_id: row.bucket_id,
      period_start: periodStart,
      assumed_value_usd: parsed,
      assumed_update_reason: reason,
    });
    setActionBusy(false);
    if (!r.ok) {
      setLocalError(r.error);
      return;
    }
    setDraft(row.bucket_id, { reason: "" });
    await reloadSummary();
  }

  async function saveActual(row: MfgOverheadPeriodBucketRow) {
    if (!plantId) return;
    const draft = drafts[row.bucket_id];
    const parsed = Number(draft?.actual);
    if (!Number.isFinite(parsed) || parsed < 0) {
      setLocalError("Actual value must be a non-negative number.");
      return;
    }

    setActionBusy(true);
    setLocalError(null);
    const r = await bookManufacturingOverheadPeriodActual({
      plant_id: plantId,
      bucket_id: row.bucket_id,
      period_start: periodStart,
      actual_value_usd: parsed,
    });
    setActionBusy(false);
    if (!r.ok) {
      setLocalError(r.error);
      return;
    }
    await reloadSummary();
  }

  async function startClose() {
    if (!plantId) return;
    setActionBusy(true);
    setLocalError(null);
    const r = await startManufacturingPeriodClose({ plant_id: plantId, period_start: periodStart });
    setActionBusy(false);
    if (!r.ok) {
      setLocalError(r.error);
      return;
    }
    await reloadSummary();
  }

  async function finalizeClose() {
    if (!plantId) return;
    setActionBusy(true);
    setLocalError(null);
    const r = await finalizeManufacturingPeriodClose({
      plant_id: plantId,
      period_start: periodStart,
      copy_actuals_to_next_period_assumed: copyActualsOnFinalize,
    });
    setActionBusy(false);
    if (!r.ok) {
      setLocalError(r.error);
      return;
    }
    await reloadSummary();
  }

  async function reopenClose() {
    if (!plantId) return;
    const reason = reopenReason.trim();
    if (!reason) {
      setLocalError("Reopen reason is required for audit trail.");
      return;
    }
    setActionBusy(true);
    setLocalError(null);
    const r = await reopenManufacturingPeriodClose({
      plant_id: plantId,
      period_start: periodStart,
      reopen_reason: reason,
    });
    setActionBusy(false);
    if (!r.ok) {
      setLocalError(r.error);
      return;
    }
    setReopenReason("");
    await reloadSummary();
  }

  return {
    plants,
    plantId,
    setPlantId,
    periodMonth,
    setPeriodMonth,
    periodStart,
    summary,
    varianceReport,
    materialsReconciliation,
    drafts,
    setDraft,
    reopenReason,
    setReopenReason,
    copyActualsOnFinalize,
    setCopyActualsOnFinalize,
    listBusy,
    actionBusy,
    localError,
    saveAssumed,
    saveActual,
    startClose,
    finalizeClose,
    reopenClose,
    reloadSummary,
  };
}
